import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2, Plus } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useCurrentIdentity } from '@/lib/identity-context';

interface ScanUrlManagerProps {
  siteId: string;
}

export function ScanUrlManager({ siteId }: ScanUrlManagerProps) {
  const { hasPermission } = useCurrentIdentity();
  const canWrite = hasPermission('sites:write');
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: queryKeys.sites.scanUrls(siteId),
    queryFn: async () => {
      const { data, error: err } = await apiClient.GET('/sites/{siteId}/scan-urls', {
        params: { path: { siteId } },
      });
      if (err) throw err;
      return data;
    },
  });

  const mutation = useMutation({
    mutationFn: async (urls: string[]) => {
      const { data, error: err } = await apiClient.PUT('/sites/{siteId}/scan-urls', {
        params: { path: { siteId } },
        body: { urls },
      });
      if (err) throw err;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sites.scanUrls(siteId) });
    },
  });

  const urls = listQuery.data?.urls ?? [];

  function handleAdd() {
    setError(null);
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (!/^https:\/\//i.test(trimmed)) {
      setError('URL must start with https://');
      return;
    }
    if (urls.includes(trimmed)) {
      setError('URL already in the list');
      return;
    }
    mutation.mutate([...urls, trimmed], {
      onSuccess: () => setDraft(''),
    });
  }

  function handleRemove(url: string) {
    mutation.mutate(urls.filter((u) => u !== url));
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>Scan URLs</CardTitle>
        <span className="text-xs text-muted-foreground">
          {urls.length} URL{urls.length === 1 ? '' : 's'}
        </span>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {urls.length === 0 && (
          <p className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-700">
            No URLs configured. Scans require at least one URL — add one before triggering.
          </p>
        )}
        <ul className="flex flex-col divide-y rounded-md border">
          {urls.map((url) => (
            <li key={url} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <span className="truncate font-mono text-xs">{url}</span>
              {canWrite && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemove(url)}
                  aria-label={`Remove ${url}`}
                  data-testid={`remove-scan-url-${url}`}
                >
                  <Trash2 className="h-4 w-4 text-muted-foreground" />
                </Button>
              )}
            </li>
          ))}
        </ul>
        {canWrite && (
          <div className="flex flex-col gap-1">
            <div className="flex gap-2">
              <Input
                placeholder="https://example.com/pricing"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={() => {
                  if (draft.trim()) handleAdd();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAdd();
                  }
                }}
                data-testid="scan-url-input"
              />
              <Button size="sm" onClick={handleAdd} disabled={mutation.isPending} data-testid="add-scan-url">
                <Plus className="h-4 w-4" />
                Add
              </Button>
            </div>
            {error && <p className="text-xs text-red-600">{error}</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
